import type { OrderStatus } from "@the-drop/types";
import { fetchWithClerkJwt } from "./fetch-with-clerk";

export type CustomerOrderLineItem = {
  id: string;
  productName: string;
  productCategory: string;
  quantity: string;
  quantityUnit: string;
  unitPriceCents: number;
  lineTotalCents: number;
};

export type CustomerOrderDetail = {
  salesDeliveryStopId: string | null;
  order: {
    id: string;
    status: OrderStatus;
    deliveryAddressLine1: string;
    deliveryAddressLine2: string | null;
    deliveryCity: string;
    deliveryState: string;
    deliveryZip: string;
    subtotalCents: number;
    taxCents: number;
    deliveryFeeCents: number;
    coSurchargeCents: number;
    totalCents: number;
    source: string;
    deliveryPaymentMethod: "ach" | "cash_at_delivery";
    createdAt: string;
    updatedAt: string;
    confirmedAt: string | null;
    cancelledAt: string | null;
    paymentAuthorizedAt: string | null;
    /** Geocoded delivery destination — drops the "delivery" pin. Null on legacy orders without coordinates. */
    deliveryLatitude: number | null;
    deliveryLongitude: number | null;
  };
  dispensary: {
    id: string;
    displayName: string;
    publicSlug: string | null;
    /** Geocoded dispensary location — drops the "pickup" pin. Null until backfilled. */
    latitude: number | null;
    longitude: number | null;
    addressLine1: string;
    city: string;
    state: string;
    zip: string;
  };
  /**
   * Latest persisted GPS point for this order's run. Seeds the
   * tracking-map driver marker on first render so the customer sees
   * a "last seen" position immediately rather than waiting for the
   * next live `driver.location` WebSocket frame. Null when the order
   * has no run yet, or no GPS rows exist for the assigned run.
   */
  lastDriverLocation: {
    latitude: number;
    longitude: number;
    /** ISO-8601. Captured wall-clock; usable as the basis for an "X min ago" caption. */
    recordedAt: string;
  } | null;
  /**
   * Late-joiner hydration for the customer ETA card. Mirrors `OrderEtaPayload` in `@the-drop/types`
   * (the WS event shape). Null pre-run (the run-start trigger from PR #2b is what produces the
   * first ETA). On page load this seeds the card; live updates over WS via `order.eta` then
   * supersede it. Coordinates-free + stops-ahead-free per the customer-disclosure rule.
   */
  etaWindow: {
    etaStart: string;
    etaEnd: string;
    computedAt: string;
    runningLate: boolean;
  } | null;
  lineItems: CustomerOrderLineItem[];
};

export async function fetchCustomerOrder(
  getToken: () => Promise<string | null>,
  orderId: string,
): Promise<CustomerOrderDetail> {
  const res = await fetchWithClerkJwt(getToken, `/api/v1/orders/${encodeURIComponent(orderId)}`);
  if (res.status === 404) {
    throw new Error("order_not_found");
  }
  if (!res.ok) {
    throw new Error(`order_http_${res.status}`);
  }
  return (await res.json()) as CustomerOrderDetail;
}
